import { toast } from "sonner";
import { persistCardPaymentPoolDefaults } from "features/wallet";
import { getImportTransactions } from "features/budget/lib/budgetTransactionGroups";
import { toastApiError } from "lib/toastApiError";
import { markImportStatementPaid } from "features/budget/api/Budget";
import type { BudgetTransaction } from "types/BudgetTransaction";
import type { TFunction } from "i18next";

export interface MarkCardStatementPaidParams {
    importId: string;
    poolByCurrency: Record<string, string | undefined>;
    cardLabel: string;
    allTransactions: BudgetTransaction[];
    username: string;
    token: string;
    paidAt?: string;
    onUpdated: (tx: BudgetTransaction) => void;
    onAdded?: (tx: BudgetTransaction) => void;
    onDeleted?: (id: string) => void;
    t: TFunction;
    successKey?: string;
    errorKey?: string;
}

export async function markCardStatementPaid({
    importId,
    poolByCurrency,
    cardLabel,
    allTransactions,
    username,
    token,
    paidAt,
    onUpdated,
    onAdded,
    onDeleted,
    t,
    successKey = "calendar.cardPaidSuccess",
}: MarkCardStatementPaidParams): Promise<void> {
    const lines = getImportTransactions(allTransactions, importId);
    if (lines.length === 0) return;

    const isUnmark = Object.values(poolByCurrency).every((poolId) => !poolId);

    const response = await markImportStatementPaid(
        username,
        { importId, poolByCurrency, paidAt },
        token,
    );

    const existingIds = new Set(allTransactions.map((tx) => tx._id));
    for (const tx of response.updatedTransactions) {
        if (existingIds.has(tx._id)) {
            onUpdated(tx);
        } else {
            onAdded?.(tx);
        }
    }

    for (const removedId of response.removedPaymentTransactionIds ?? []) {
        onDeleted?.(removedId);
    }

    if (!isUnmark) {
        const persisted = Object.fromEntries(
            Object.entries(poolByCurrency).filter((entry): entry is [string, string] => Boolean(entry[1])),
        );
        persistCardPaymentPoolDefaults(username, cardLabel, persisted);
    }

    toast.success(t(successKey));
}

export async function markCardStatementPaidSafe(
    params: MarkCardStatementPaidParams,
): Promise<boolean> {
    try {
        await markCardStatementPaid(params);
        return true;
    } catch (err) {
        toastApiError(err, params.t, params.errorKey ?? "budget.editError");
        return false;
    }
}
